/**
 * Play engine — SVG-based one-stroke interaction.
 *
 * Owns: graph state, drag/tap input, edge traversal validation, win detect,
 * undo, idle hints. Returns a controller the host screen wires to its
 * chrome (timer, progress, win callback).
 *
 * Rendering: SVG (≤ 70 elements → DOM is fine; gives us free a11y +
 * hit testing). The trace path is a single <polyline>.
 *
 * Per UI-SPEC §3 G2 + GAME-SPEC §1.1 mechanics:
 *  - Touch any odd-degree vertex (or any vertex if 0 odd-degree).
 *  - Drag toward a neighbor connected by an unused edge.
 *  - When pointer enters that vertex's hit-radius, commit the edge.
 *  - Long-press a vertex = reset path (only if at start or no edges done).
 */

import type { Edge, Puzzle, Vertex } from '../engine/types';

export interface PlayConfig {
  puzzle: Puzzle;
  onProgress?: (done: number, total: number) => void;
  onWin?: (result: { timeMs: number; attempts: number }) => void;
  onIdle?: (stage: 'wiggle' | 'hint') => void;
  /** Color theme; defaults to rarity color. */
  accentColor?: string;
  /** Reduced motion override (else system). */
  reduceMotion?: boolean;
}

export interface PlayController {
  el: SVGSVGElement;
  destroy(): void;
  reset(): void;
  /** Get elapsed time in ms. */
  elapsed(): number;
  /** Force-finalize for testing. */
  _state(): { traversed: number[]; head: number | null };
}

const VIEW = 320; // viewBox is 320x320 internally
const VERT_R = 9;
const HIT_R = 24; // touch hit radius
const STROKE_W = 3;

function ns<K extends keyof SVGElementTagNameMap>(name: K): SVGElementTagNameMap[K] {
  return document.createElementNS('http://www.w3.org/2000/svg', name);
}

export function mountPlay(host: HTMLElement, cfg: PlayConfig): PlayController {
  const { puzzle } = cfg;
  const startTime = performance.now();
  const traversed: number[] = []; // edge ids in order
  let head: number | null = null; // current vertex id
  let attempts = 0;
  let pointerActive = false;
  let pointerId: number | null = null;
  let dragX = 0;
  let dragY = 0;
  let won = false;
  let idleTimer1: ReturnType<typeof setTimeout> | null = null;
  let idleTimer2: ReturnType<typeof setTimeout> | null = null;
  let longPressTimer: ReturnType<typeof setTimeout> | null = null;
  const accent = cfg.accentColor ?? rarityColor(puzzle.rarity);

  const svg = ns('svg');
  svg.setAttribute('viewBox', `0 0 ${VIEW} ${VIEW}`);
  svg.setAttribute(
    'aria-label',
    `Puzzle: ${puzzle.vertices.length} vertices, ${puzzle.edges.length} edges`,
  );
  svg.style.touchAction = 'none';
  svg.style.userSelect = 'none';
  svg.style.display = 'block';
  svg.style.width = '100%';
  svg.style.height = '100%';

  // Layout in pixels.
  const pad = 36;
  const coord = (v: Vertex): [number, number] => [
    pad + v.x * (VIEW - 2 * pad),
    pad + v.y * (VIEW - 2 * pad),
  ];

  // Build edge index for fast neighbor lookup.
  const edgesByVertex = new Map<number, Array<{ edgeId: number; other: number }>>();
  for (const e of puzzle.edges) {
    if (!edgesByVertex.has(e.a)) edgesByVertex.set(e.a, []);
    if (!edgesByVertex.has(e.b)) edgesByVertex.set(e.b, []);
    edgesByVertex.get(e.a)!.push({ edgeId: e.id, other: e.b });
    edgesByVertex.get(e.b)!.push({ edgeId: e.id, other: e.a });
  }

  // Render base layer.
  const gEdges = ns('g');
  gEdges.classList.add('edges');
  const gVerts = ns('g');
  gVerts.classList.add('vertices');
  svg.appendChild(gEdges);
  svg.appendChild(gVerts);

  const edgeEls = new Map<number, SVGLineElement>();
  for (const e of puzzle.edges) {
    const [ax, ay] = coord(puzzle.vertices[e.a]!);
    const [bx, by] = coord(puzzle.vertices[e.b]!);
    const line = ns('line');
    line.setAttribute('x1', String(ax));
    line.setAttribute('y1', String(ay));
    line.setAttribute('x2', String(bx));
    line.setAttribute('y2', String(by));
    line.setAttribute('stroke', 'var(--edge-rest, #CCC)');
    line.setAttribute('stroke-width', String(STROKE_W));
    line.setAttribute('stroke-linecap', 'round');
    line.setAttribute('data-edge', String(e.id));
    edgeEls.set(e.id, line);
    gEdges.appendChild(line);
  }

  // Active drag preview line.
  const activeLine = ns('line');
  activeLine.setAttribute('stroke', accent);
  activeLine.setAttribute('stroke-width', String(STROKE_W + 1));
  activeLine.setAttribute('stroke-linecap', 'round');
  activeLine.setAttribute('stroke-dasharray', '8 4');
  activeLine.setAttribute('opacity', '0');
  if (!cfg.reduceMotion) {
    activeLine.style.animation = 'fudeto-dash-flow 600ms linear infinite';
  }
  gEdges.appendChild(activeLine);

  const vertEls = new Map<number, SVGCircleElement>();
  const hitEls = new Map<number, SVGCircleElement>();
  for (const v of puzzle.vertices) {
    const [x, y] = coord(v);
    const c = ns('circle');
    c.setAttribute('cx', String(x));
    c.setAttribute('cy', String(y));
    c.setAttribute('r', String(VERT_R));
    c.setAttribute('fill', 'var(--ink, #1A1A1A)');
    c.setAttribute('data-v', String(v.id));
    vertEls.set(v.id, c);
    gVerts.appendChild(c);

    const hit = ns('circle');
    hit.setAttribute('cx', String(x));
    hit.setAttribute('cy', String(y));
    hit.setAttribute('r', String(HIT_R));
    hit.setAttribute('fill', 'transparent');
    hit.setAttribute('data-v-hit', String(v.id));
    hit.style.cursor = 'pointer';
    hitEls.set(v.id, hit);
    gVerts.appendChild(hit);
  }

  // Pulse on start-eligible vertices (odd-degree ones, or all if 0 odd).
  highlightStartVertices();

  // Status ring around head.
  const headRing = ns('circle');
  headRing.setAttribute('r', String(VERT_R + 4));
  headRing.setAttribute('fill', 'none');
  headRing.setAttribute('stroke', accent);
  headRing.setAttribute('stroke-width', '2');
  headRing.setAttribute('opacity', '0');
  gVerts.appendChild(headRing);

  // Wire input.
  svg.addEventListener('pointerdown', onPointerDown);
  svg.addEventListener('pointermove', onPointerMove);
  svg.addEventListener('pointerup', onPointerUp);
  svg.addEventListener('pointercancel', onPointerUp);
  svg.addEventListener('contextmenu', (ev) => ev.preventDefault());

  host.appendChild(svg);

  ensureKeyframes();
  resetIdle();

  function highlightStartVertices(): void {
    const degree = new Map<number, number>();
    for (const e of puzzle.edges) {
      degree.set(e.a, (degree.get(e.a) ?? 0) + 1);
      degree.set(e.b, (degree.get(e.b) ?? 0) + 1);
    }
    const odds: number[] = [];
    for (const [v, d] of degree) if (d % 2 === 1) odds.push(v);
    const eligible = odds.length > 0 ? odds : Array.from(degree.keys());
    for (const v of eligible) {
      const c = vertEls.get(v);
      if (!c) continue;
      c.setAttribute('fill', accent);
      c.style.transition = 'transform 240ms ease';
    }
  }

  function svgPoint(ev: PointerEvent): [number, number] {
    const rect = svg.getBoundingClientRect();
    const x = ((ev.clientX - rect.left) / rect.width) * VIEW;
    const y = ((ev.clientY - rect.top) / rect.height) * VIEW;
    return [x, y];
  }

  function findVertexAt(x: number, y: number, radius = HIT_R): number | null {
    for (const v of puzzle.vertices) {
      const [vx, vy] = coord(v);
      const dx = vx - x;
      const dy = vy - y;
      if (dx * dx + dy * dy <= radius * radius) return v.id;
    }
    return null;
  }

  function eligibleStart(v: number): boolean {
    if (traversed.length > 0) return false;
    const degree = new Map<number, number>();
    for (const e of puzzle.edges) {
      degree.set(e.a, (degree.get(e.a) ?? 0) + 1);
      degree.set(e.b, (degree.get(e.b) ?? 0) + 1);
    }
    let oddCount = 0;
    for (const d of degree.values()) if (d % 2 === 1) oddCount++;
    if (oddCount === 0) return true; // any vertex
    return (degree.get(v) ?? 0) % 2 === 1;
  }

  function findEdgeBetween(a: number, b: number): number | null {
    const list = edgesByVertex.get(a) ?? [];
    for (const item of list) {
      if (item.other === b && !traversed.includes(item.edgeId)) return item.edgeId;
    }
    return null;
  }

  function onPointerDown(ev: PointerEvent): void {
    if (won) return;
    ev.preventDefault();
    const [x, y] = svgPoint(ev);
    const v = findVertexAt(x, y);
    if (v == null) return;
    svg.setPointerCapture(ev.pointerId);
    pointerId = ev.pointerId;
    pointerActive = true;
    dragX = x;
    dragY = y;

    if (head === null) {
      if (!eligibleStart(v)) {
        flashVertex(v);
        pointerActive = false;
        return;
      }
      head = v;
      updateHeadRing();
      schedulingLongPress(v);
    } else if (v === head) {
      schedulingLongPress(v);
    } else {
      // Tap on a non-head vertex — try commit edge from head.
      tryAdvanceTo(v);
    }
    showActiveLine();
    resetIdle();
  }

  function onPointerMove(ev: PointerEvent): void {
    if (!pointerActive || won || pointerId !== ev.pointerId) return;
    const [x, y] = svgPoint(ev);
    dragX = x;
    dragY = y;
    cancelLongPress();
    if (head !== null) {
      // If we crossed into a new vertex hit area, attempt to advance.
      const v = findVertexAt(x, y, HIT_R);
      if (v != null && v !== head) tryAdvanceTo(v);
    }
    updateActiveLine();
    resetIdle();
  }

  function onPointerUp(ev: PointerEvent): void {
    if (pointerId !== ev.pointerId) return;
    if (svg.hasPointerCapture(ev.pointerId)) svg.releasePointerCapture(ev.pointerId);
    pointerActive = false;
    pointerId = null;
    cancelLongPress();
    hideActiveLine();
  }

  function tryAdvanceTo(target: number): void {
    if (head === null) return;
    const edgeId = findEdgeBetween(head, target);
    if (edgeId == null) {
      attempts++;
      flashVertex(target);
      return;
    }
    commitEdge(edgeId, target);
  }

  function commitEdge(edgeId: number, target: number): void {
    traversed.push(edgeId);
    head = target;
    const line = edgeEls.get(edgeId);
    if (line) {
      line.setAttribute('stroke', accent);
      line.style.transition = 'stroke 180ms ease';
    }
    updateHeadRing();
    cfg.onProgress?.(traversed.length, puzzle.edges.length);
    if (traversed.length === puzzle.edges.length) {
      win();
    }
  }

  function win(): void {
    won = true;
    cancelLongPress();
    stopIdle();
    const timeMs = Math.round(performance.now() - startTime);
    // Soft completion glow on all edges.
    for (const e of edgeEls.values()) {
      e.setAttribute('stroke', accent);
      e.style.opacity = '1';
    }
    cfg.onWin?.({ timeMs, attempts });
  }

  function updateActiveLine(): void {
    if (head === null) {
      activeLine.setAttribute('opacity', '0');
      return;
    }
    const [hx, hy] = coord(puzzle.vertices[head]!);
    activeLine.setAttribute('x1', String(hx));
    activeLine.setAttribute('y1', String(hy));
    activeLine.setAttribute('x2', String(dragX));
    activeLine.setAttribute('y2', String(dragY));
    activeLine.setAttribute('opacity', '1');
  }

  function showActiveLine(): void {
    updateActiveLine();
  }
  function hideActiveLine(): void {
    activeLine.setAttribute('opacity', '0');
  }

  function updateHeadRing(): void {
    if (head === null) {
      headRing.setAttribute('opacity', '0');
      return;
    }
    const [x, y] = coord(puzzle.vertices[head]!);
    headRing.setAttribute('cx', String(x));
    headRing.setAttribute('cy', String(y));
    headRing.setAttribute('opacity', '1');
    // mark the head vertex with accent
    for (const [vid, c] of vertEls) {
      c.setAttribute('fill', vid === head ? accent : 'var(--ink, #1A1A1A)');
    }
  }

  function flashVertex(v: number): void {
    const c = vertEls.get(v);
    if (!c) return;
    c.style.transition = 'transform 180ms ease, opacity 180ms ease';
    c.style.opacity = '0.4';
    setTimeout(() => {
      c.style.opacity = '1';
    }, 200);
  }

  function schedulingLongPress(v: number): void {
    cancelLongPress();
    longPressTimer = setTimeout(() => {
      if (head !== null && traversed.length === 0) {
        head = v;
        updateHeadRing();
      } else if (traversed.length === 0) {
        head = v;
        updateHeadRing();
      } else {
        // Reset path entirely.
        controller.reset();
      }
    }, 500);
  }

  function cancelLongPress(): void {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      longPressTimer = null;
    }
  }

  function resetIdle(): void {
    if (idleTimer1) clearTimeout(idleTimer1);
    if (idleTimer2) clearTimeout(idleTimer2);
    idleTimer1 = setTimeout(() => cfg.onIdle?.('wiggle'), 10000);
    idleTimer2 = setTimeout(() => cfg.onIdle?.('hint'), 30000);
  }
  function stopIdle(): void {
    if (idleTimer1) clearTimeout(idleTimer1);
    if (idleTimer2) clearTimeout(idleTimer2);
    idleTimer1 = null;
    idleTimer2 = null;
  }

  const controller: PlayController = {
    el: svg,
    destroy() {
      cancelLongPress();
      stopIdle();
      svg.remove();
    },
    reset() {
      traversed.length = 0;
      head = null;
      attempts = 0;
      won = false;
      for (const [, l] of edgeEls) {
        l.setAttribute('stroke', 'var(--edge-rest, #CCC)');
        l.style.opacity = '1';
      }
      for (const [, c] of vertEls) {
        c.setAttribute('fill', 'var(--ink, #1A1A1A)');
      }
      highlightStartVertices();
      headRing.setAttribute('opacity', '0');
      activeLine.setAttribute('opacity', '0');
      cfg.onProgress?.(0, puzzle.edges.length);
      resetIdle();
    },
    elapsed() {
      return performance.now() - startTime;
    },
    _state() {
      return { traversed: traversed.slice(), head };
    },
  };
  return controller;
}

function rarityColor(r: Edge[] extends never ? never : Puzzle['rarity']): string {
  switch (r) {
    case 'common':
      return 'var(--rarity-common, #22C55E)';
    case 'uncommon':
      return 'var(--rarity-uncommon, #3B82F6)';
    case 'rare':
      return 'var(--rarity-rare, #EF4444)';
    case 'epic':
      return 'var(--rarity-epic, #A855F7)';
    case 'legendary':
      return 'var(--rarity-legendary, #EAB308)';
  }
}

let keyframesInjected = false;
function ensureKeyframes(): void {
  if (keyframesInjected) return;
  keyframesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
@keyframes fudeto-dash-flow { to { stroke-dashoffset: -12; } }
@keyframes fudeto-vertex-pulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.3); opacity: 0.4; }
}`;
  document.head.appendChild(style);
}
